import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PayoutAvatar } from "@/components/admin/payouts/primitives";
import { Check, CircleNotch, Warning } from "@phosphor-icons/react";
import { fmtMoney, PLAN_LABEL } from "./subscription-ui";
import type { LedgerListRow } from "@/types";
import { throwIfNotOk } from "@/lib/api-error";

export function MarkBankTransferPaidDialog({
  ledger,
  onClose,
}: {
  ledger: LedgerListRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [externalId, setExternalId] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/subscription-ledger/${ledger.id}/mark-paid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paidAt, externalId: externalId || undefined, notes: notes || undefined }),
      });
      await throwIfNotOk(res, "Greška pri potvrdi uplate.");
      qc.invalidateQueries({ queryKey: ["admin", "subscription-ledger"] });
      qc.invalidateQueries({ queryKey: ["admin", "subscription-overview"] });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={Check}
          title="Označi uplatu plaćenom"
          description="Unesite datum i internu referencu bankovne uplate."
          tone="success"
        />
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <div className="flex items-center gap-3 rounded-xl bg-muted/40 px-3.5 py-3">
          <PayoutAvatar
            name={ledger.therapistName ?? ledger.therapistFullName}
            tone="primary"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-foreground">
              {ledger.therapistName ?? ledger.therapistFullName}
            </div>
            <div className="text-xs text-muted-foreground">
              {PLAN_LABEL[ledger.planType]} · {fmtMoney(ledger.amountGross)}{" "}
              {ledger.currency}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-2.5 rounded-xl bg-rose-50 px-3.5 py-3 text-[13px] leading-snug text-rose-800 ring-1 ring-inset ring-rose-200/70">
            <Warning className="mt-0.5 size-4 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="space-y-1.5">
            <Label>Datum uplate</Label>
            <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Bank reference (opcionalno)</Label>
            <Input value={externalId} onChange={(e) => setExternalId(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Napomena</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Otkaži</Button>
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <>
              <CircleNotch className="size-4 animate-spin" />
              Snimam...
            </>
          ) : (
            <>
              <Check className="size-4" />
              Potvrdi uplatu
            </>
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
